import {Box} from 'src/design/atoms/Box';
import {useEffect, useMemo, useState} from 'react';
import type {SelectOption} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateCourseDefinitionMutation,
  useGetCourseDefinitionListQuery,
} from 'src/redux/queries/courseDefinition';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';

import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CourseFormMeta} from '../../../../app/training/courseFormMeta';
import {
  CreateDefaultCourseDefinition,
  MapCourseFormDataToCourseDefinitionRequest,
} from '../../../../app/training/courseDefaults';
import {TrainingDocumentTitle} from '../../../../app/training/courseContentValidation';
import {TrainingIcon} from '../../../../app/training/coursePills';
import {CourseSettingsPanel} from '../ViewCourseDefinition/components/Build/CourseSettingsPanel';
import {CreateCourseEditRedirect} from './components/CreateCourseEditRedirect';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

/**
 * Course creation only. Editing an existing course happens in place on the
 * Course Workspace's Build tab (docs/plans/training-admin-overhaul.md Wave
 * 5) — there is no separate update route/page anymore. Renders the same
 * grouped Course Settings UI as the workspace's Build tab so the two flows
 * look and behave consistently.
 */
export const CreateCoursePage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const [data] = useState(CreateDefaultCourseDefinition());
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const [isSavePending, setIsSavePending] = useState(false);

  const [courses, setCourses] = useState<SelectOption[]>([]);

  const formMeta = useMemo(() => CourseFormMeta(courses), [courses]);

  const form = useProactForm({formMeta});

  const {data: courseListData, error: courseListError} =
    useGetCourseDefinitionListQuery({start: 0, length: 100});

  const [createCourseDefinition] = useCreateCourseDefinitionMutation();

  useEffect(() => {
    if (courseListError) {
      setAlert(parseErrorMessage(courseListError), 'error');
    }
  }, [courseListError]);

  useEffect(() => {
    if (!courseListData) return;
    const remap =
      courseListData.data?.map(
        course => ({id: course.id, label: course.name} as SelectOption)
      ) ?? [];
    setCourses(remap);
  }, [courseListData]);

  const handleSave = async () => {
    if (!(await form.validateForm())) {
      return;
    }

    try {
      setIsSavePending(true);

      // Create carries no content: the Build tab authors and validates it.
      // Submitting a placeholder lesson here fails the backend's lesson
      // validation (empty video URL), which has no valid unauthored default.
      const model = MapCourseFormDataToCourseDefinitionRequest(
        {...toFormDataV2(data), ...form.getFieldValues()},
        []
      );

      const response = await createCourseDefinition(model).unwrap();

      setAlert(`Course ${response?.name} was successfully created.`, 'success');
      navigate(PathUtility.TrainingCourseView(String(response.id), 'build'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'create-course',
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        <PageHeader>
          <PageHeader.Title icon={TrainingIcon}>New Course</PageHeader.Title>
          <PageHeader.Actions>
            <PageHeader.Action
              variant="save"
              onClick={handleSave}
              loading={isSavePending}>
              Save
            </PageHeader.Action>
          </PageHeader.Actions>
        </PageHeader>

        <ProactForm form={form}>
          <CourseSettingsPanel />
        </ProactForm>
      </Box>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.TrainingCourseCreate(),
  element: <CreateCoursePage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.Create,
  isPrivate: true,
});

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.TrainingCourseEdit(':definitionId'),
  element: <CreateCourseEditRedirect />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
